import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import parse from "html-react-parser";
import { useCartData } from "@/zustand/cartStore";
import { useProductData } from "@/zustand/productStore";
import { useQuizModalStore } from "@/zustand/quizModalStore";
import {
  getDiscountSettings,
  isDiscountActive,
  isDiscountEnabledForSelection,
  isDiscountExpired,
  isDiscountPopupMode,
  replaceDiscountPercentVariable,
} from "@/utils/discounts";
import s from "./DiscountPopup.module.scss";

/*
 * Discount popup for the product pack's "Discount popup" mode.
 *
 * Mirrors the lander pop-up (src/Popups, assets/css/cc-popup.css) — same Figma
 * frames — but its CTA applies the product pack's discount code exactly like the
 * order summary "Apply" button does, so promo prices, the order payload and the
 * Konnektive coupon all follow the existing discount-code path.
 *
 * Shown once per browser session. Accepting is remembered for the session too,
 * so a reload (a declined card, a return from PayPal) keeps the discounted prices.
 */

const STORAGE_KEY = `cc_discount_popup_${window.postId}`;
const STATE_ACCEPTED = "accepted";
const STATE_DISMISSED = "dismissed";

// Same rule as the lander pop-up: never open on top of another modal (the age
// gate can run on checkouts too), check back with a backoff instead.
const OTHER_MODALS = '[aria-modal="true"], .age-verification-overlay';
const MODAL_RECHECK_MS = 500;
const MODAL_RECHECK_MAX_MS = 8000;
const MODAL_RECHECK_LIMIT = 10;

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const readState = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const writeState = (state) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, state);
  } catch (error) {
    /* private mode — falls back to once per page load */
  }
};

const otherModalOpen = (own) =>
  Array.from(document.querySelectorAll(OTHER_MODALS)).some((el) => {
    if (own && (el === own || own.contains(el))) return false;
    if (el.hasAttribute("hidden")) return false;

    const style = window.getComputedStyle(el);
    return style.display !== "none" && style.visibility !== "hidden";
  });

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M9.19095 7.99998L15.7534 1.43756C16.0822 1.10869 16.0822 0.575499 15.7534 0.246656C15.4245 -0.0822186 14.8913 -0.0822186 14.5624 0.246656L7.99999 6.80908L1.43756 0.246656C1.10869 -0.0822186 0.575499 -0.0822186 0.246656 0.246656C-0.0821873 0.57553 -0.0822186 1.10872 0.246656 1.43756L6.80908 7.99998L0.246656 14.5624C-0.0822186 14.8913 -0.0822186 15.4245 0.246656 15.7533C0.57553 16.0822 1.10872 16.0822 1.43756 15.7533L7.99999 9.19089L14.5624 15.7533C14.8913 16.0822 15.4245 16.0822 15.7533 15.7533C16.0822 15.4244 16.0822 14.8913 15.7533 14.5624L9.19095 7.99998Z"
      fill="currentColor"
    />
  </svg>
);

const DiscountPopup = () => {
  const productData = useProductData();
  const { selectedProduct, isSubscribe, discountCode, isDiscountApplied, handleSetDiscountCode } = useCartData();
  const { isQuizCompleted, isQuizModalOpen } = useQuizModalStore();
  const [isOpen, setIsOpen] = useState(false);
  // Bumped when the popup steps aside for another modal, so it queues up again.
  const [openAttempt, setOpenAttempt] = useState(0);
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);

  const content = window.post_template?.checkout_v4_discount_popup ?? {};
  const generalSettings = window.general_settings ?? {};
  const quizShow = !!window.post_template?.checkout_v4_quiz?.quiz_show;

  const settings = getDiscountSettings(productData, isSubscribe);
  const code = String(settings?.discount_code ?? "").trim();
  const isAvailable =
    isDiscountPopupMode(productData) &&
    isDiscountEnabledForSelection(productData, isSubscribe) &&
    code !== "" &&
    !isDiscountExpired(settings);
  const discountActive = isDiscountActive(productData, isSubscribe, discountCode, isDiscountApplied);
  const quizDone = !quizShow || (isQuizCompleted && !isQuizModalOpen);

  // Keep an accepted discount across reloads and the one-time / subscription
  // switch, which swaps in a different code.
  useEffect(() => {
    if (!isAvailable || readState() !== STATE_ACCEPTED) return;
    handleSetDiscountCode(code);
  }, [isAvailable, code]);

  useEffect(() => {
    if (!isAvailable || discountActive || !quizDone || readState()) return;

    let timerId;
    let modalWaits = 0;

    const tryOpen = () => {
      if (otherModalOpen(overlayRef.current)) {
        if (modalWaits >= MODAL_RECHECK_LIMIT) return;

        timerId = setTimeout(tryOpen, Math.min(MODAL_RECHECK_MS * 2 ** modalWaits, MODAL_RECHECK_MAX_MS));
        modalWaits++;
        return;
      }

      setIsOpen(true);
    };

    const delayMs = Math.max(0, Number(content.delay_seconds) || 0) * 1000;
    timerId = setTimeout(tryOpen, delayMs);

    return () => clearTimeout(timerId);
  }, [isAvailable, discountActive, quizDone, openAttempt]);

  useEffect(() => {
    if (!isOpen) return;

    const lastFocused = document.activeElement;
    document.body.style.setProperty("overflow", "hidden", "important");

    const focusable = () => Array.from(dialogRef.current?.querySelectorAll(FOCUSABLE) ?? []);
    (focusable()[0] ?? dialogRef.current)?.focus();

    const onKeydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        dismiss();
        return;
      }

      if (event.key !== "Tab") return;

      const items = focusable();
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // The age gate can open after us (its check is a REST call) and inert the
    // whole body, leaving us visible but dead. Step aside without remembering a
    // dismissal, and queue up again behind it.
    const inertWatcher = new MutationObserver(() => {
      if (overlayRef.current?.closest("[inert]")) {
        setIsOpen(false);
        setOpenAttempt((attempt) => attempt + 1);
      }
    });

    document.addEventListener("keydown", onKeydown);
    inertWatcher.observe(document.body, { attributes: true, attributeFilter: ["inert"], subtree: true });

    return () => {
      document.removeEventListener("keydown", onKeydown);
      inertWatcher.disconnect();
      document.body.style.removeProperty("overflow");
      if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
    };
  }, [isOpen]);

  const dismiss = () => {
    writeState(STATE_DISMISSED);
    setIsOpen(false);
  };

  const accept = () => {
    writeState(STATE_ACCEPTED);
    handleSetDiscountCode(code);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  const image = content.image?.url ? content.image : null;
  const showLogo = content.show_logo !== false;
  const useGlobalLogo = content.use_global_logo !== false;
  const logoUrl = useGlobalLogo ? generalSettings.logo : content.logo_image?.url;
  const logoAlt = (!useGlobalLogo && content.logo_image?.alt) || generalSettings.brand_name || "";
  // Before a package is picked, the first one stands in for the percentage.
  const percentProduct = selectedProduct ?? productData?.products?.[0];
  const withPercent = (text) => replaceDiscountPercentVariable(text, percentProduct);
  const heading = withPercent(content.tr_heading || "");
  const description = withPercent(content.tr_description || "");
  const ctaLabel = withPercent(
    content.tr_cta_label || window.post_template?.checkout_v4_summary?.tr_checkout_v4_discount_apply || "Apply"
  );

  const cardClasses = [s.card];
  if (image) {
    cardClasses.push(s["card--hasImage"]);
    if (content.show_image_tablet !== false) cardClasses.push(s["card--imageTablet"]);
    if (content.show_image_mobile === true) cardClasses.push(s["card--imageMobile"]);
  }

  // Background and CTA colours accept gradients, so they are applied as `background`.
  const colorVars = {
    "--discount-popup-bg": content.bg_color || undefined,
    "--discount-popup-heading-color": content.heading_color || undefined,
    "--discount-popup-text-color": content.text_color || undefined,
    "--discount-popup-cta-bg": content.cta_bg_color || undefined,
    "--discount-popup-cta-color": content.cta_label_color || undefined,
  };

  return createPortal(
    <div
      ref={overlayRef}
      className={s.overlay}
      onClick={(event) => {
        if (event.target === event.currentTarget) dismiss();
      }}
    >
      <div
        ref={dialogRef}
        className={cardClasses.join(" ")}
        style={colorVars}
        role="dialog"
        aria-modal="true"
        {...(heading ? { "aria-labelledby": "cc-discount-popup-title" } : { "aria-label": ctaLabel })}
        tabIndex={-1}
      >
        {image && (
          <div className={s.visual}>
            <img src={image.url} alt={image.alt || ""} loading="eager" decoding="async" />
          </div>
        )}
        <div className={s.content}>
          <button type="button" className={s.close} aria-label="Close" onClick={dismiss}>
            <CloseIcon />
          </button>
          {showLogo && logoUrl && (
            <div className={s.logo}>
              <img src={logoUrl} alt={logoAlt} loading="eager" decoding="async" />
            </div>
          )}
          {(heading || description) && (
            <div className={s.text}>
              {heading && (
                <h2 className={s.title} id="cc-discount-popup-title">
                  {heading}
                </h2>
              )}
              {description && <div className={s.copy}>{parse(description)}</div>}
            </div>
          )}
          <button type="button" className={s.cta} onClick={accept}>
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DiscountPopup;
